import { z } from 'zod';

import {
  CashMovementDirectionSchema,
  CashMovementKindSchema,
  CashMovementSourceTypeSchema,
} from './cash-movement.schema.js';
import {
  CashDenominationSchema,
  CashForceCloseModeSchema,
  CashOpeningModeSchema,
  CashSessionCloseModeSchema,
  CashSessionStatusSchema,
} from './cash-sessions.schema.js';
import {
  cashReportTimestampSchema,
  CashReportActorRefDtoSchema,
  CashReportCashLocationRefDtoSchema,
  CashReportMovementCategorySchema,
  CashReportPhysicalEffectSchema,
  CashReportPurchasePayoutRefDtoSchema,
  CashReportSessionRefDtoSchema,
  CashReportSourceRefDtoSchema,
  CashReportVarianceActionRefDtoSchema,
  CashReportVarianceCaseRefDtoSchema,
  validateCashReportDateRange,
} from './cash-report-common.schema.js';
import type {
  CashMovementHistoryReportRowDto,
  CashSessionHistoryReportRowDto,
  ListCashMovementHistoryReportRequest,
  ListCashMovementHistoryReportResponse,
  ListCashSessionHistoryReportRequest,
  ListCashSessionHistoryReportResponse,
} from './cash-report-history.dto.js';

export const ListCashSessionHistoryReportRequestSchema: z.ZodType<ListCashSessionHistoryReportRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid().optional(),
      locationId: z.uuid().optional(),
      stationId: z.uuid().optional(),
      cashLocationId: z.uuid().optional(),
      status: CashSessionStatusSchema.optional(),
      actorUserId: z.uuid().optional(),
      from: cashReportTimestampSchema.optional(),
      to: cashReportTimestampSchema.optional(),
    })
    .superRefine(validateCashReportDateRange);

export const CashSessionHistoryReportRowDtoSchema: z.ZodType<CashSessionHistoryReportRowDto> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema,
    status: CashSessionStatusSchema,
    openedAt: cashReportTimestampSchema,
    closedAt: cashReportTimestampSchema.nullable(),
    openedBy: CashReportActorRefDtoSchema.nullable(),
    closedBy: CashReportActorRefDtoSchema.nullable(),
    openingMode: CashOpeningModeSchema,
    openingBasisAmountMinor: z.number().int(),
    openingCountedAmountMinor: z.number().int().nullable(),
    openingCountDenominations: z.array(CashDenominationSchema).nullable().optional(),
    closeMode: CashSessionCloseModeSchema.nullable(),
    forceCloseMode: CashForceCloseModeSchema.nullable(),
    closeExpectedCashMinor: z.number().int().nullable(),
    closeCountedCashMinor: z.number().int().nullable(),
    closeVarianceAmountMinor: z.number().int().nullable(),
    closingDenominations: z.array(CashDenominationSchema).nullable().optional(),
    currentExpectedCashMinor: z.number().int().nullable(),
    isForceCounted: z.boolean(),
    isForceUnverified: z.boolean(),
    auditRisk: z.boolean(),
    isLive: z.boolean(),
  });

export const ListCashSessionHistoryReportResponseSchema: z.ZodType<ListCashSessionHistoryReportResponse> =
  z.object({ items: z.array(CashSessionHistoryReportRowDtoSchema) });

export const ListCashMovementHistoryReportRequestSchema: z.ZodType<ListCashMovementHistoryReportRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid().optional(),
      locationId: z.uuid().optional(),
      stationId: z.uuid().optional(),
      cashLocationId: z.uuid().optional(),
      cashSessionId: z.uuid().optional(),
      movementKind: CashMovementKindSchema.optional(),
      reportCategory: CashReportMovementCategorySchema.optional(),
      direction: CashMovementDirectionSchema.optional(),
      sourceType: CashMovementSourceTypeSchema.optional(),
      sourceId: z.uuid().optional(),
      actorUserId: z.uuid().optional(),
      from: cashReportTimestampSchema.optional(),
      to: cashReportTimestampSchema.optional(),
      cursor: z.string().trim().min(1).max(512).optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    })
    .superRefine(validateCashReportDateRange);

export const CashMovementHistoryReportRowDtoSchema: z.ZodType<CashMovementHistoryReportRowDto> =
  z.object({
    movementId: z.uuid(),
    companyId: z.uuid(),
    occurredAt: cashReportTimestampSchema,
    movementKind: CashMovementKindSchema,
    reportCategory: CashReportMovementCategorySchema,
    direction: CashMovementDirectionSchema,
    physicalEffect: CashReportPhysicalEffectSchema,
    amountMinor: z.number().int().nonnegative(),
    description: z.string().trim().min(1).max(500).nullable(),
    reasonCode: z.string().trim().min(1).max(100).nullable(),
    isReversal: z.boolean(),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema.nullable(),
    varianceCase: CashReportVarianceCaseRefDtoSchema.nullable(),
    varianceAction: CashReportVarianceActionRefDtoSchema.nullable(),
    purchasePayout: CashReportPurchasePayoutRefDtoSchema.nullable(),
    source: CashReportSourceRefDtoSchema.nullable(),
    fundingSourceCategoryId: z.uuid().nullable().optional(),
    fundingSourceCategoryCodeSnapshot: z.string().trim().min(1).max(80).nullable().optional(),
    fundingSourceCategoryDisplayNameSnapshot: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .nullable()
      .optional(),
    actor: CashReportActorRefDtoSchema.nullable(),
  });

export const ListCashMovementHistoryReportResponseSchema: z.ZodType<ListCashMovementHistoryReportResponse> =
  z.object({
    items: z.array(CashMovementHistoryReportRowDtoSchema),
    nextCursor: z.string().trim().min(1).max(512).nullable(),
    total: z.number().int().nonnegative(),
  });
